import { useCallback } from "react";
import { createDebugLogger } from "@utils/debugLog";

const log = createDebugLogger("auth-popup:popup");

export interface AuthPopupResult {
  cancelled: boolean;
  state?: string;
  error?: string;
  errorDescription?: string;
  /** Source needs an org/environment picked before it can finish authorising. */
  requiresOrgSelection?: boolean;
  /** Connection the org selection applies to. Only set with requiresOrgSelection. */
  connectionId?: string;
}

const parseCallbackLocation = (
  location: string,
): Omit<AuthPopupResult, "cancelled"> => {
  try {
    const url = new URL(location, window.location.origin);
    return {
      state: url.searchParams.get("state") || undefined,
      error: url.searchParams.get("error") || undefined,
      errorDescription: url.searchParams.get("error_description") || undefined,
      requiresOrgSelection:
        url.searchParams.get("requiresOrgSelection") === "true",
      connectionId: url.searchParams.get("connectionId") || undefined,
    };
  } catch {
    return {};
  }
};

// We rely on window.opener in the popup to postMessage back to the opener, so
// window.open() must NOT use "noopener"/"noreferrer" (either one nulls out
// window.opener). Instead, the popup is marked via its window.name and that
// is checked before ever closing it — this prevents window.close() from
// nuking the host app when this code runs as the top-level window (typed
// URL, refresh, or a host that doesn't support real popups).
const AUTH_POPUP_WINDOW_NAME = "onetasklist-auth-popup";

// A provider page served with Cross-Origin-Opener-Policy: same-origin (Slack's are) moves the
// popup into a new browsing context group. That nulls the popup's window.opener and clears its
// window.name for good, and makes the app's handle on the popup read closed while the window is
// still open. A same-origin BroadcastChannel still reaches the app, so the callback page posts
// there too, and a popup that reads closed is not taken as cancelled straight away.
const AUTH_POPUP_CHANNEL_NAME = "onetasklist-auth-popup";

// A popup the user really closed hands focus back to the app; this long with focus confirms it.
const CLOSED_FOCUS_CONFIRM_MS = 1500;

// A popup that reads closed while the app stays unfocused may still be signing in. Give up after this.
const CLOSED_GRACE_MS = 5 * 60 * 1000;

const POLL_INTERVAL_MS = 500;

const openAuthChannel = (): BroadcastChannel | null =>
  typeof BroadcastChannel === "undefined"
    ? null
    : new BroadcastChannel(AUTH_POPUP_CHANNEL_NAME);

const readAuthorisedLocation = (data: unknown): string | null => {
  const message = data as Record<string, unknown> | null;
  return message &&
    typeof message === "object" &&
    message["action"] === "authorised" &&
    typeof message["location"] === "string"
    ? message["location"]
    : null;
};

export const canCloseAuthPopup = (): boolean =>
  window.name === AUTH_POPUP_WINDOW_NAME;

const waitForPopup = (popup: Window | null): Promise<AuthPopupResult> =>
  new Promise((resolve) => {
    if (!popup) {
      log("waitForPopup: window.open returned null (likely blocked by the browser)");
      resolve({ cancelled: true });
      return;
    }

    let settled = false;
    let pollId = 0;
    let closedSince: number | null = null;
    let focusedSince: number | null = null;
    const channel = openAuthChannel();

    const settle = (result: AuthPopupResult): void => {
      if (settled) return;
      settled = true;
      log("waitForPopup: settling", result);
      window.removeEventListener("message", onMessage);
      window.clearInterval(pollId);
      channel?.close();
      resolve(result);
    };

    const onAuthorised = (location: string, via: string): void => {
      log("waitForPopup: received authorised message", { location, via });
      settle({ cancelled: false, ...parseCallbackLocation(location) });
    };

    const onMessage = (event: MessageEvent): void => {
      if (event.origin !== window.location.origin) {
        log("waitForPopup: ignoring message from unexpected origin", {
          origin: event.origin,
        });
        return;
      }

      const location = readAuthorisedLocation(event.data);
      if (location) {
        onAuthorised(location, "opener");
      }
    };

    // BroadcastChannel only carries messages from this origin, so there is no origin to check.
    if (channel) {
      channel.onmessage = (event: MessageEvent) => {
        const location = readAuthorisedLocation(event.data);
        if (location) {
          onAuthorised(location, "channel");
        }
      };
    }

    window.addEventListener("message", onMessage);
    pollId = window.setInterval(() => {
      if (!popup.closed) {
        closedSince = null;
        focusedSince = null;
        return;
      }

      const now = Date.now();
      closedSince ??= now;
      focusedSince = document.hasFocus() ? (focusedSince ?? now) : null;

      if (focusedSince !== null && now - focusedSince >= CLOSED_FOCUS_CONFIRM_MS) {
        log("waitForPopup: popup was closed without an authorised message");
        settle({ cancelled: true });
      } else if (now - closedSince >= CLOSED_GRACE_MS) {
        log("waitForPopup: popup has read closed for the whole grace period");
        settle({ cancelled: true });
      }
    }, POLL_INTERVAL_MS);
  });

const POPUP_WIDTH = 520;
const POPUP_HEIGHT = 600;

/**
 * Centres the popup on the browser window rather than letting it default to
 * the top-left corner of the primary screen.
 *
 * screenX/screenY are the opener's position on the virtual desktop, so the
 * popup lands on whichever monitor the user is actually working on — those
 * coordinates are legitimately negative for a screen to the left of the
 * primary one, which is why nothing here clamps them to zero.
 */
const buildPopupFeatures = (): string => {
  const left = Math.round(
    window.screenX + (window.outerWidth - POPUP_WIDTH) / 2,
  );
  const top = Math.round(
    window.screenY + (window.outerHeight - POPUP_HEIGHT) / 2,
  );

  return `height=${POPUP_HEIGHT},width=${POPUP_WIDTH},left=${left},top=${top}`;
};

/**
 * Abstracts browser popup + postMessage auth so the implementation can be
 * swapped for Teams SDK (or any other host) without touching call sites.
 *
 * Opener side  — call openAuthPopup() to launch the flow.
 * Popup side   — call notifyComplete() from the callback page.
 */
export const useAuthPopup = () => {
  const openAuthPopup = useCallback(
    (url: string, _displayName: string): Promise<AuthPopupResult> => {
      log("openAuthPopup: opening popup", { url });
      const popup = window.open(
        url,
        AUTH_POPUP_WINDOW_NAME,
        buildPopupFeatures(),
      );
      return waitForPopup(popup);
    },
    [],
  );

  // Posts the "authorised" message back to the opener, and on the auth
  // BroadcastChannel for when a provider's Cross-Origin-Opener-Policy has
  // severed the opener. The app settles on whichever arrives first. Does NOT
  // close the popup itself — closing is a separate, explicit step (see
  // closeAuthPopup) driven by the callback page once it has confirmed this ran.
  const notifyComplete = useCallback((): void => {
    const message = { action: "authorised", location: window.location.href };
    log("notifyComplete: posting authorised message", {
      hasOpener: !!window.opener,
    });
    window.opener?.postMessage(message, window.location.origin);

    const channel = openAuthChannel();
    channel?.postMessage(message);
    channel?.close();
  }, []);

  const closeAuthPopup = useCallback((): void => {
    const canClose = canCloseAuthPopup();
    log("closeAuthPopup", { canClose });
    if (canClose) {
      window.close();
    }
  }, []);

  return { openAuthPopup, notifyComplete, closeAuthPopup };
};
